import Phaser from 'phaser';
import { toScreen } from './Grid';
import type { RoomDef } from './rooms';

/** Decorative, deliberately non-interactive room dressing. The gameplay layer owns all objects and collisions. */
export function drawRoomScenery(scene: Phaser.Scene, room: RoomDef, originX: number, originY: number, skills:string[]=[]): Phaser.GameObjects.Container {
  const c = scene.add.container(0, 0).setDepth(-10);
  const g = scene.add.graphics();
  c.add(g);
  const p = (x: number, y: number) => toScreen(x, y, originX, originY);
  const corners = [p(0, 0), p(room.cols, 0), p(room.cols, room.rows), p(0, room.rows)];
  const minX = Math.min(...corners.map(q => q.sx)) - 2200, maxX = Math.max(...corners.map(q => q.sx)) + 2200;
  const minY = Math.min(...corners.map(q => q.sy)) - 1600, maxY = Math.max(...corners.map(q => q.sy)) + 1600;
  const field = room.id === 'field';
  const cellar = room.id === 'cellar';
  const creamery = room.id === 'creamery';
  const barn = room.id === 'barn';

  g.fillStyle(field ? 0x71965e : cellar ? 0x302d36 : barn ? 0x70533d : 0x60706a, 1).fillRect(minX, minY, maxX - minX, maxY - minY);
  // Quiet surroundings: an outdoor apron, or the earth and foundation around the cellar.
  if (field || barn || creamery) {

    for (let i = 0; i < 18; i++) {
      const x = minX + 37 + ((i * 83) % Math.max(80, maxX - minX - 70));
      const y = minY + 30 + ((i * 47) % Math.max(80, maxY - minY - 60));
      g.lineStyle(2, field ? 0x547b4f : 0x66513c, 0.45).lineBetween(x, y, x + 7, y - 10);
      g.lineBetween(x + 7, y - 10, x + 13, y);
    }
  } else {
    g.fillStyle(0x51454a, 0.8).fillRect(minX, minY, maxX - minX, maxY - minY);
    g.lineStyle(5, 0x211f27, 0.65).strokeRect(minX + 20, minY + 20, maxX - minX - 40, maxY - minY - 40);
  }
  // Farmyard scenery fills the camera, independent of gameplay bounds.
  for(let i=0;i<360;i++){
    const x=minX+((i*197)%Math.floor(maxX-minX)), y=minY+((i*139)%Math.floor(maxY-minY));
    g.lineStyle(1,cellar?0x83746a:0x48694a,.22).lineBetween(x,y,x+7,y-3);
    if(i%18===0&&!cellar){g.fillStyle(0x496547,.5).fillEllipse(x,y,75,35);g.fillStyle(0x71935b,.6).fillEllipse(x-15,y-9,50,35);}
    if(i%21===0&&cellar){g.fillStyle(0x655851,.3).fillEllipse(x,y,70,24);}
  }
  if(!cellar){
    const gate=p(room.cols,3.5);
    g.lineStyle(60,0xb7a17a,.45).lineBetween(gate.sx,gate.sy,gate.sx+420,gate.sy+150);
    for(let i=0;i<35;i++){const x=gate.sx+(i*83)%420,y=gate.sy+((i*61)%130);g.fillStyle(0xccbf9d,.28).fillEllipse(x,y,7,3);}
    for(const [x,y] of [[-2,1],[-1,7],[10,1],[10,5],[5,-3],[12,7]]){
      const q=p(x,y);g.fillStyle(0x334837,.18).fillEllipse(q.sx+20,q.sy+9,90,26);
      g.lineStyle(9,0x65503b).lineBetween(q.sx,q.sy,q.sx,q.sy-60);
      g.fillStyle(0x446948).fillEllipse(q.sx,q.sy-78,76,65).fillEllipse(q.sx-23,q.sy-62,55,43).fillEllipse(q.sx+26,q.sy-67,55,48);
      g.fillStyle(0x71915a,.8).fillEllipse(q.sx-13,q.sy-92,45,29);
    }
    const shed=p(-2,4);g.fillStyle(0x6c5341,.8).fillRect(shed.sx-190,shed.sy-60,160,85);
    g.fillStyle(0x735044,.9).fillTriangle(shed.sx-210,shed.sy-60,shed.sx-110,shed.sy-115,shed.sx-10,shed.sy-60);
    g.fillStyle(0x443e35,.8).fillRect(shed.sx-145,shed.sy-22,40,47);
    g.lineStyle(2,0x9b7956,.7);for(let i=0;i<6;i++)g.lineBetween(shed.sx-188+i*27,shed.sy-58,shed.sx-188+i*27,shed.sy+23);
    g.fillStyle(0x8fa9a8).fillRect(shed.sx-77,shed.sy-40,27,22);g.lineStyle(3,0x5a4a36).strokeRect(shed.sx-77,shed.sy-40,27,22);
    for(let i=0;i<4;i++){g.fillStyle(0x8e7350).fillRoundedRect(shed.sx-220+i*28,shed.sy+49,34,17,5);g.lineStyle(2,0x665039).strokeRoundedRect(shed.sx-220+i*28,shed.sy+49,34,17,5);}
    for(let i=0;i<9;i++){g.fillStyle(0x55734b,.8).fillEllipse(shed.sx-250+i*45,shed.sy+100,60,28);}
  }
  if(cellar){
    const excavation=[p(-1,-1),p(room.cols+1,-1),p(room.cols+1,room.rows+1),p(-1,room.rows+1)];
    g.fillStyle(0x685549).fillPoints(excavation.map(q=>({x:q.sx,y:q.sy})),true);
    g.lineStyle(7,0x85705b,.6).strokePoints(excavation.map(q=>({x:q.sx,y:q.sy})),true);
    const foundation=[p(-.2,-.2),p(room.cols+.2,-.2),p(room.cols+.2,room.rows+.2),p(-.2,room.rows+.2)];g.fillStyle(0x56515b).fillPoints(foundation.map(q=>({x:q.sx,y:q.sy})),true);
    for(let i=0;i<15;i++){const q=p(-.65,i/2);g.lineStyle(2,0x352d2b,.5).lineBetween(q.sx,q.sy,q.sx-15,q.sy+8);const r=p(room.cols+.6,i/2);g.lineBetween(r.sx,r.sy,r.sx+15,r.sy-8);}
  }
  // Main ground plane, a single readable silhouette (especially important in the pasture).
  g.fillStyle(field ? 0x8eaf70 : room.floorA, 1).beginPath();
  g.moveTo(corners[0].sx, corners[0].sy); corners.slice(1).forEach(q => g.lineTo(q.sx, q.sy)); g.closePath().fillPath();
  g.lineStyle(3, field ? 0x65885b : 0x8b735d, 0.7).strokePath();

  if(!field) drawWalls(g,room,p,skills);
  if (field) drawField(g, room, p);
  else if (cellar) drawCellar(g, room, p);
  else if (creamery) drawTiles(g, room, p);
  else drawBarn(g, room, p);
  return c;
}

function drawBarn(g: Phaser.GameObjects.Graphics, room: RoomDef, p: (x: number, y: number) => { sx: number; sy: number }): void {
  for (let y = 0; y <= room.rows; y += 1) { const a = p(0, y), b = p(room.cols, y); g.lineStyle(3, 0x795238, 0.5).lineBetween(a.sx, a.sy, b.sx, b.sy); }
  for (let i = 0; i < 11; i++) { const a = p(i % room.cols, (i * 3) % room.rows), b = p((i % room.cols) + 1, (i * 3) % room.rows); g.lineStyle(2, 0x8f6545, 0.55).lineBetween(a.sx, a.sy + 9, b.sx, b.sy + 9); }
  for(let i=0;i<70;i++){const q=p(.15+((i*43)%850)/100,.15+((i*29)%650)/100);g.lineStyle(2,0xdac386,.6).lineBetween(q.sx,q.sy,q.sx+10,q.sy-4);}
  g.lineStyle(12, 0x4f382e, 0.8); const a = p(0, 0), b = p(room.cols, 0); g.lineBetween(a.sx, a.sy, b.sx, b.sy);
}

function drawTiles(g: Phaser.GameObjects.Graphics, room: RoomDef, p: (x: number, y: number) => { sx: number; sy: number }): void {
  for (let y = 0; y <= room.rows; y++) { const a = p(0, y), b = p(room.cols, y); g.lineStyle(2, 0x9aafa3, 0.65).lineBetween(a.sx, a.sy, b.sx, b.sy); }
  for (let x = 0; x <= room.cols; x++) { const a = p(x, 0), b = p(x, room.rows); g.lineStyle(2, 0x9aafa3, 0.65).lineBetween(a.sx, a.sy, b.sx, b.sy); }
  g.fillStyle(0x9b775c, 0.2).fillCircle(p(3.5, 3.2).sx, p(3.5, 3.2).sy, 22).fillCircle(p(1.4, 5.1).sx, p(1.4, 5.1).sy, 13);
}

function drawCellar(g: Phaser.GameObjects.Graphics, room: RoomDef, p: (x: number, y: number) => { sx: number; sy: number }): void {
  for (let y = 0; y <= room.rows; y++) { const a = p(0, y), b = p(room.cols, y); g.lineStyle(5, 0x514d58, 0.7).lineBetween(a.sx, a.sy, b.sx, b.sy); }
  for (let x = 0; x <= room.cols; x++) { const a = p(x, 0), b = p(x, room.rows); g.lineStyle(3, 0x4a4650, 0.65).lineBetween(a.sx, a.sy, b.sx, b.sy); }
  g.fillStyle(0x273039, 0.35).fillEllipse(p(4.5, 2.5).sx, p(4.5, 2.5).sy, 70, 24);
}

function drawField(g: Phaser.GameObjects.Graphics,room:RoomDef,p:(x:number,y:number)=>{sx:number;sy:number}):void {
 const gap=(side:string,i:number)=>room.objects.some(o=>o.kind==='door'&&(side==='top'?o.y===0&&o.x===i:side==='bottom'?o.y===room.rows-1&&o.x===i:side==='left'?o.x===0&&o.y===i:o.x===room.cols-1&&o.y===i));
 for(const side of ['top','bottom','left','right']){
  const horizontal=side==='top'||side==='bottom',n=horizontal?room.cols:room.rows;
  for(let i=0;i<n;i++){
   const a=p(horizontal?i:side==='left'?0:room.cols,horizontal?(side==='top'?0:room.rows):i);
   const b=p(horizontal?i+1:side==='left'?0:room.cols,horizontal?(side==='top'?0:room.rows):i+1);
   g.lineStyle(5,0x634932).lineBetween(a.sx,a.sy,a.sx,a.sy-24);
   g.lineStyle(2,0x98704b).lineBetween(a.sx+2,a.sy,a.sx+2,a.sy-24);
   if(!gap(side,i)){g.lineStyle(4,0x8a6847).lineBetween(a.sx,a.sy-8,b.sx,b.sy-8).lineBetween(a.sx,a.sy-20,b.sx,b.sy-20);}
   else {g.lineStyle(3,0xc6a979).lineBetween(a.sx,a.sy+5,b.sx,b.sy+5);}
  }
 }
 const trailA=p(4.5,6.5),trailB=p(4.6,3.2);g.lineStyle(30,0xb7aa7d,.4).lineBetween(trailA.sx,trailA.sy,trailB.sx,trailB.sy);
 for(let i=0;i<420;i++){const x=.12+((i*137)%880)/100,y=.12+((i*83)%680)/100,q=p(x,y);g.lineStyle(1, i%2?0x6a9557:0xa8c486,.65).lineBetween(q.sx,q.sy,q.sx+3,q.sy-5).lineBetween(q.sx+3,q.sy-5,q.sx+7,q.sy);if(i%19===0){g.fillStyle(i%3?0xf1dea2:0xd7bfd5).fillCircle(q.sx+4,q.sy-5,2);}}
 const scar=p(6.2,2);g.fillStyle(0x685d44,.25).fillEllipse(scar.sx,scar.sy,120,40);
}
function drawWalls(g:Phaser.GameObjects.Graphics,room:RoomDef,p:(x:number,y:number)=>{sx:number;sy:number},skills:string[]):void {
 const stone=room.id==='cellar',wood=room.id==='barn',wall=stone?0x77717c:wood?0xba8b62:0xe2d5bd;
 for(const [x1,y1,x2,y2] of [[0,0,room.cols,0],[0,0,0,room.rows]]){
  const a=p(x1,y1),b=p(x2,y2),height=85;
  g.fillStyle(wall).fillPoints([{x:a.sx,y:a.sy},{x:b.sx,y:b.sy},{x:b.sx,y:b.sy-height},{x:a.sx,y:a.sy-height}],true);
  g.lineStyle(3,stone?0x504b58:0x73543f,.8).lineBetween(a.sx,a.sy,b.sx,b.sy).lineBetween(a.sx,a.sy-height,b.sx,b.sy-height);
  const count=wood?18:14;
  for(let i=0;i<=count;i++){const t=i/count,x=a.sx+(b.sx-a.sx)*t,y=a.sy+(b.sy-a.sy)*t;g.lineStyle(wood?2:1,wood?0x835c40:0x968d87,.5).lineBetween(x,y,x,y-height);}
  if(stone||!wood)for(let h=20;h<height;h+=22)g.lineStyle(stone?2:1,stone?0x514c58:0xc1b6a7,.6).lineBetween(a.sx,a.sy-h,b.sx,b.sy-h);
  if(wood)for(let i=0;i<=3;i++){const t=i/3,x=a.sx+(b.sx-a.sx)*t,y=a.sy+(b.sy-a.sy)*t;g.lineStyle(9,0x684632).lineBetween(x,y,x,y-height);}
 }

 for(const o of room.objects.filter(o=>o.kind==='door'&&(!o.requiresSkill||skills.includes(o.requiresSkill))&&(o.y===0||o.x===0))){
   const top=o.y===0,a=top?p(o.x+.08,0):p(0,o.y+.08),b=top?p(o.x+.92,0):p(0,o.y+.92);
   g.fillStyle(0x403d33).fillPoints([{x:a.sx,y:a.sy},{x:b.sx,y:b.sy},{x:b.sx,y:b.sy-62},{x:a.sx,y:a.sy-62}],true);
   g.lineStyle(4,0x9c805c).lineBetween(a.sx,a.sy,a.sx,a.sy-62).lineBetween(b.sx,b.sy,b.sx,b.sy-62).lineBetween(a.sx,a.sy-62,b.sx,b.sy-62);
 }
 const window=p(6,0);g.fillStyle(stone?0x3e434d:0x54777f).fillRect(window.sx-35,window.sy-70,70,44);g.lineStyle(5,0x80654c).strokeRect(window.sx-35,window.sy-70,70,44);g.lineStyle(3,0xb9ab83).lineBetween(window.sx,window.sy-70,window.sx,window.sy-26);
 if(stone){g.fillStyle(0xf3c979,.10).fillEllipse(window.sx,window.sy,180,90);g.fillStyle(0xf3c979).fillCircle(window.sx-80,window.sy-45,7);}
 if(!wood&&!stone){g.lineStyle(4,0x8a9da0).lineBetween(window.sx+80,window.sy-55,window.sx+160,window.sy-15);g.fillStyle(0x82694c).fillRect(window.sx+95,window.sy-44,75,8);}
}
